import { compact, get, map, split, trim } from "lodash";
import React, { useEffect, useState } from "react";

import type { VerificationConfig } from "@platform/dmv-api";

import ButtonPrimary from "../ButtonPrimary";
import Modal from "../Modal";
import Toggle from "../Toggle";

export type VerificationChannel = "email" | "sms";
export type VerificationChannelConfig = NonNullable<
  VerificationConfig[VerificationChannel]
>;

type VerificationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (_config: VerificationChannelConfig) => void;
  channel: VerificationChannel;
  initialData?: VerificationChannelConfig | null;
};

export const DEFAULT_VERIFICATION_MESSAGE =
  "Hi {{firstName}},\n\n{{clientName}} uses Axle to quickly and securely verify your insurance information. Please use the link below to get started:\n\n{{ignitionUri}}\n\n- {{clientName}}";

const E164_REGEX = /^\+[1-9]\d{9,14}$/;

const inputClassName =
  "w-full rounded border border-gray-300 p-2.5 text-sm placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black";

const parseDaysAfter = (input: string): number[] | null => {
  const parts = compact(map(split(input, ","), trim));
  const days = map(parts, (part) => (/^\d+$/.test(part) ? Number(part) : NaN));
  return days.some((day) => !day) ? null : days;
};

const VerificationModal = ({
  isOpen,
  onClose,
  onSave,
  channel,
  initialData,
}: VerificationModalProps) => {
  const isEditMode = !!initialData;
  const isEmail = channel === "email";
  const label = isEmail ? "Email" : "SMS";

  const [enabled, setEnabled] = useState(true);
  const [subjectOrSender, setSubjectOrSender] = useState("");
  const [message, setMessage] = useState("");
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [daysAfterInput, setDaysAfterInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setEnabled(get(initialData, "enabled", true));
    setSubjectOrSender(
      get(initialData, isEmail ? "subject" : "sender", "") as string
    );
    setMessage(
      get(initialData, "message", DEFAULT_VERIFICATION_MESSAGE) as string
    );
    setRemindersEnabled(get(initialData, "reminders.enabled", false));
    setDaysAfterInput(get(initialData, "reminders.daysAfter", []).join(", "));
    setValidationError(null);
  }, [isOpen, initialData, isEmail]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const value = subjectOrSender.trim();
    if (!value) {
      setValidationError(`${isEmail ? "Subject line" : "Sender"} is required.`);
      return;
    }
    if (!isEmail && !E164_REGEX.test(value)) {
      setValidationError("Sender must be E.164, e.g. +15551234567.");
      return;
    }
    if (!message.trim()) {
      setValidationError("Content is required.");
      return;
    }
    const daysAfter = parseDaysAfter(daysAfterInput);
    if (!daysAfter) {
      setValidationError(
        "Reminder days must be positive whole numbers separated by commas."
      );
      return;
    }
    if (remindersEnabled && daysAfter.length === 0) {
      setValidationError(
        "Add at least one reminder day or turn reminders off."
      );
      return;
    }

    const hasReminders = remindersEnabled || !!initialData?.reminders;
    const base = {
      enabled,
      message,
      ...(hasReminders && {
        reminders: { enabled: remindersEnabled, daysAfter },
      }),
    };
    onSave(isEmail ? { ...base, subject: value } : { ...base, sender: value });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${isEditMode ? "Edit" : "Add"} ${label} Verification`}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-5">
        <div className="flex flex-col gap-y-2">
          <label className="text-sm font-medium text-gray-700">
            {isEmail ? "Subject line" : "Sender"}
          </label>
          <input
            type="text"
            value={subjectOrSender}
            onChange={(e) => {
              setSubjectOrSender(e.target.value);
              setValidationError(null);
            }}
            placeholder={
              isEmail
                ? "[Axle] Verify your insurance information"
                : "+15551234567"
            }
            className={inputClassName}
          />
        </div>

        <div className="flex flex-col gap-y-2">
          <label className="text-sm font-medium text-gray-700">Content</label>
          <textarea
            rows={8}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setValidationError(null);
            }}
            className={`${inputClassName} font-mono`}
          />
          <p className="text-xs text-gray-500">
            Use {"{{firstName}}"} and {"{{clientName}}"}
            {isEmail ? "" : ", and {{ignitionUri}}"} in the content to
            personalize it for each recipient.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Enabled</label>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Reminders</label>
          <Toggle checked={remindersEnabled} onChange={setRemindersEnabled} />
        </div>

        <div className="flex flex-col gap-y-2">
          <label className="text-sm font-medium text-gray-700">
            Reminder days after sending
            <span className="font-normal text-gray-500 ml-1">
              (comma-separated)
            </span>
          </label>
          <input
            type="text"
            value={daysAfterInput}
            onChange={(e) => {
              setDaysAfterInput(e.target.value);
              setValidationError(null);
            }}
            placeholder="1, 3, 7"
            className={inputClassName}
          />
        </div>

        {validationError && (
          <p className="text-sm text-red-600">{validationError}</p>
        )}

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <ButtonPrimary
            text={isEditMode ? "Save" : "Add"}
            type="submit"
            icon={undefined}
            className=""
          />
        </div>
      </form>
    </Modal>
  );
};

export default VerificationModal;
